import { useAuth0 } from '@auth0/auth0-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useStateMachine } from 'little-state-machine';
import { ProductID } from '@lib/ProductID';
import useSelector from '@redux/typedHooks';
import { useQueryParams } from '@hooks/useQueryParameters';
import { actions as idpActions } from '@redux/modules/internationalDrivingPermit';
import { createServerFieldMappings } from '../../lib/createServerFieldMappings';
import { INTERNATIONAL_DRIVING_PERMIT_FORM_NAME } from '../../lib/constants';
import { updateAction } from '../../lib/updateAction';
import { InternationalDrivingPermitSchema } from '../../schemas/InternationalDrivingPermit';
import { useTrackingStep1 } from '../../utils/useIDPTracking';

export const defaultProduct = { productId: undefined, amount: '1' };

export const useDrivingPermitLogic = (currentStep?: number) => {
  const dispatch = useDispatch();
  const {
    actions,
    state: { internationalDrivingPermitForm },
  } = useStateMachine({ updateAction });
  const [hasSelected, setHasSelected] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const previousStepRef = useRef<number | undefined>(undefined);
  const queryParameters = useQueryParams();
  const { isAuthenticated } = useAuth0();
  const isUpdating = useSelector((state) => Object.values(state.internationalDrivingPermit.isUpdating)[0] || null);
  const products = useSelector((state) => state.internationalDrivingPermit.data.eligibleProducts || null);
  const ownedProducts = useSelector((state) => state.internationalDrivingPermit.data.ownedProducts || null);
  const countries = useSelector((state) => state.idpCountries.data);
  const serverErrors = useSelector((state) => state.order.validation?.validationResults);
  const form = useForm({ resolver: yupResolver(InternationalDrivingPermitSchema) });
  const {
    clearErrors,
    getValues,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = form;
  const product = watch('product');
  const pickupAtOffice = watch('pickupAtOffice');

  useEffect(() => setIsMounted(true), []);

  useEffect(() => {
    const isEnteringStepOne = currentStep === 1 && previousStepRef.current !== 1;
    const hadSelectedMembership = !!internationalDrivingPermitForm?.membership?.productId;
    if (isEnteringStepOne) {
      actions.updateAction({
        formName: INTERNATIONAL_DRIVING_PERMIT_FORM_NAME,
        data: { ...internationalDrivingPermitForm, membership: undefined },
      });
      if (hadSelectedMembership) dispatch(idpActions.getInternationalDrivingPermit.request(undefined));
    }
    previousStepRef.current = currentStep;
  }, [actions, currentStep, dispatch, internationalDrivingPermitForm]);

  useEffect(() => {
    const queryProduct = queryParameters?.product;
    if (products && queryProduct && products[queryProduct]) {
      setValue('product', { productId: queryProduct, amount: '1', isAuthenticated });
    } else if (typeof queryProduct === 'undefined') {
      const storedProduct = getValues('product');
      if (storedProduct) {
        setValue('product', { productId: storedProduct.productId, amount: '1', isAuthenticated });
      }
    }
  }, [queryParameters?.product, isAuthenticated, getValues, products, setValue]);

  useEffect(() => {
    if (!product || !products || !hasSelected) return;
    if (
      ownedProducts &&
      (ProductID.Idp1Year in ownedProducts ||
        ProductID.Idp3Year in ownedProducts ||
        ProductID.Idp1and3Year in ownedProducts)
    ) {
      setError('product', {
        type: 'manual',
        message: 'Du har allerede bestilt Internasjonalt førerkort 1- og 3-årig.',
      });
    } else if (!products[product.productId]) {
      setError('product', {
        type: 'manual',
        message: 'Oops, vi har problemer med motoren... Ta kontakt med kundesenteret hvis problemet fortsetter!',
      });
    } else {
      clearErrors('product');
    }
  }, [product, products, hasSelected, clearErrors, ownedProducts, setError]);

  useEffect(() => {
    if (product?.productId !== undefined) setHasSelected(true);
  }, [product]);

  useEffect(() => {
    if (!serverErrors) return;
    const amount = Number(internationalDrivingPermitForm?.product?.amount ?? defaultProduct.amount);
    const mapping = createServerFieldMappings(amount);
    serverErrors.forEach((error) => {
      if (mapping.form1[error.field]) setError('product', { type: 'server', message: error.message });
    });
  }, [internationalDrivingPermitForm?.product?.amount, serverErrors, setError]);

  useTrackingStep1(product?.productId);

  const onSubmit = (data: any) => {
    actions.updateAction({
      formName: INTERNATIONAL_DRIVING_PERMIT_FORM_NAME,
      data: { ...internationalDrivingPermitForm, ...data },
    });
  };

  const selectedCountryObjects = selectedCountries
    .map((countryName) => countries?.find((country) => country.countryName === countryName))
    .filter(Boolean);

  return {
    form,
    errors,
    products,
    isUpdating,
    isMounted,
    hasSelected,
    pickupAtOffice,
    internationalDrivingPermitForm,
    options: countries?.map((country) => ({ label: country.countryName, value: country.countryName })),
    selectedCountryObjects,
    addCountry: (country: string) => setSelectedCountries((current) => [...current, country]),
    removeCountry: (country: string) =>
      setSelectedCountries((current) => current.filter((countryName) => countryName !== country)),
    onSubmit,
  };
};
